import { motion } from 'motion/react';
import './Skeleton.css';

/**
 * Animated loading skeletons for record-driven pages.
 *
 * Each `.skeleton` block is a flat parchment-midtone rectangle that
 * slowly breathes between two palette colors (`--rule-soft` and
 * `--rule`), with a small opacity pulse layered on top. No gradients —
 * the rest of the site is drawn in solid colors and hairline rules, so
 * the placeholders match that voice. Because the keyframe loop returns
 * to its starting color, the cycle has no visible seam.
 *
 * The outer `SkeletonShell` adds a short Motion fade-in (and fade-out
 * when wrapped in `AnimatePresence`) so the skeleton column doesn't
 * snap into or out of place when real content arrives.
 *
 * Multi-item skeletons (feed, TOC, grid, comments) are staggered via
 * CSS `:nth-child` animation-delays so rows breathe slightly out of
 * phase instead of pulsing in lockstep. The breathing respects
 * `prefers-reduced-motion` (CSS falls back to a static, slightly faded
 * block).
 */

/**
 * One animated rectangle. Pass any subset of width/height/radius — the
 * defaults give you a single line of text on the current line-height.
 */
export function Skeleton({
  width,
  height = '1em',
  radius,
  className = '',
  block = false,
  style,
  ...rest
}) {
  return (
    <span
      className={`skeleton ${block ? 'skeleton-block' : ''} ${className}`.trim()}
      style={{
        width,
        height,
        borderRadius: radius,
        ...style,
      }}
      aria-hidden="true"
      {...rest}
    />
  );
}

/**
 * Several text-line skeletons stacked into a paragraph. The last line is
 * shorter so it reads more like real prose than a brick of text.
 */
export function SkeletonText({
  lines = 3,
  lineHeight = '0.85em',
  lastLineWidth = '60%',
  className = '',
}) {
  return (
    <span className={`skeleton-text ${className}`.trim()} aria-hidden="true">
      {Array.from({ length: Math.max(1, lines) }, (_, i) => (
        <Skeleton
          key={i}
          width={i === lines - 1 ? lastLineWidth : '100%'}
          height={lineHeight}
        />
      ))}
    </span>
  );
}

/**
 * Outer Motion fade for any skeleton. Used so the skeleton column fades
 * in (and, when wrapped in <AnimatePresence>, fades back out as the real
 * content swaps in). Adds aria-busy on the section so assistive tech
 * announces "loading" even though the inner blocks are aria-hidden.
 */
export function SkeletonShell({ children, className = '', label = 'Loading' }) {
  return (
    <motion.div
      className={`skeleton-shell ${className}`.trim()}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      role="status"
      aria-busy="true"
      aria-label={label}
    >
      {children}
    </motion.div>
  );
}

/* -------------------------------------------------------------------- */
/* Page-shaped variants                                                  */
/* -------------------------------------------------------------------- */

/**
 * Skeleton for any feed list (Home, Posting, Logging). Mirrors the
 * `.feed-item` two-column grid (4.5rem verb badge + body) so the
 * placeholder doesn't reflow once the real items arrive.
 *
 * Each row's body has a couple of staggered text lines plus a tiny
 * timestamp on the right.
 */
export function FeedSkeleton({ rows = 6, label = 'Loading feed', ledger = false }) {
  if (ledger) {
    // The home ledger's own three columns (verb | body | time) on its
    // hairline rows, so the first rows to land replace bars where they
    // stand instead of pushing a status line down.
    return (
      <SkeletonShell label={label}>
        <div className="skeleton-ledger-head">
          <Skeleton className="skeleton-ledger-day" />
        </div>
        <ul className="skeleton-ledger">
          {Array.from({ length: rows }, (_, i) => (
            <li key={i} className="skeleton-ledger-row">
              <Skeleton className="skeleton-ledger-verb" />
              <div className="skeleton-feed-body">
                <Skeleton className="skeleton-feed-text" style={{ width: `${70 + ((i * 7) % 26)}%` }} />
                {i % 3 === 1 && <Skeleton block style={{ width: `${38 + ((i * 13) % 32)}%`, height: '0.85em' }} />}
              </div>
              <Skeleton className="skeleton-ledger-time" />
            </li>
          ))}
        </ul>
      </SkeletonShell>
    );
  }
  return (
    <SkeletonShell label={label}>
      <ul className="skeleton-feed">
        {Array.from({ length: rows }, (_, i) => {
          const longLine = 78 + ((i * 7) % 18); // 78 – 96 % to look organic
          const shortLine = 38 + ((i * 13) % 32); // 38 – 70 %
          return (
            <li key={i} className="skeleton-feed-item">
              <Skeleton className="skeleton-feed-verb" />
              <div className="skeleton-feed-body">
                <div className="skeleton-feed-row">
                  <Skeleton
                    className="skeleton-feed-text"
                    style={{ width: `${longLine}%` }}
                  />
                  <Skeleton className="skeleton-feed-time" />
                </div>
                {i % 3 !== 0 && (
                  <Skeleton
                    block
                    style={{ width: `${shortLine}%`, height: '0.85em' }}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </SkeletonShell>
  );
}

/**
 * Skeleton for a FlatLedger index (blogging, creating's index, curating's
 * index): one hairline row per entry, the title at the left and its time or
 * count flush right, with creating's category column ahead of the title when
 * `kinded`.
 *
 * The list and its rows wear FlatLedger's own classes (Feed.css), so the
 * column tracks, gaps, rules and line boxes are the real ones and only the
 * text is replaced by bars. A copy of those numbers in this file is how the
 * blog index's placeholder came to draw a number column and a summary line
 * the index had long since dropped.
 */
export function FlatLedgerSkeleton({
  rows = 6,
  kinded = false,
  timeWidth = '3.5rem',
  label = 'Loading',
}) {
  return (
    <SkeletonShell label={label}>
      <ol
        className={`feed-list feed-ledger feed-ledger-flat${kinded ? ' feed-ledger-flat-kinded' : ''}`}
        aria-hidden="true"
      >
        {Array.from({ length: rows }, (_, i) => (
          <li key={i} className="feed-item feed-item-ledger">
            {kinded && (
              <span className="ledger-verb ledger-kind-col">
                <Skeleton width={`${3.5 + ((i * 5) % 3) * 0.75}rem`} height="0.8em" />
              </span>
            )}
            <div className="ledger-body">
              <p className="ledger-text">
                <Skeleton width={`${42 + ((i * 17) % 40)}%`} height="0.8em" />
              </p>
            </div>
            <span className="ledger-time">
              <Skeleton width={timeWidth} height="0.8em" />
            </span>
          </li>
        ))}
      </ol>
    </SkeletonShell>
  );
}

/**
 * Skeleton for /creating's plates: a 4:3 cover above the "software · 2026"
 * line and the title, on the page's own `.creating-plates` grid (Creating.css),
 * so the tracks and gutters are the ones the covers land in.
 *
 * The cover passes `height="auto"`: `Skeleton` writes its height as an inline
 * style, which beats the class's `aspect-ratio` and flattened the older grid
 * skeleton's 4:3 thumbs into 16px strips.
 */
export function CreatingPlatesSkeleton({ cells = 6 }) {
  return (
    <SkeletonShell label="Loading works">
      <ul className="creating-plates" aria-hidden="true">
        {Array.from({ length: cells }, (_, i) => (
          <li key={i} className="creating-plate">
            <div className="creating-plate-link">
              <Skeleton block className="skeleton-plate-cover" height="auto" />
              <span className="creating-plate-caption">
                <span className="creating-plate-kind">
                  <Skeleton width={`${6 + ((i * 3) % 4)}em`} height="0.75em" />
                </span>
                <span className="creating-plate-title">
                  <Skeleton width={`${50 + ((i * 13) % 35)}%`} height="0.75em" />
                  {i % 2 === 0 && (
                    <span className="skeleton-plate-title-more">
                      <Skeleton width="45%" height="0.75em" />
                    </span>
                  )}
                </span>
              </span>
            </div>
          </li>
        ))}
      </ul>
    </SkeletonShell>
  );
}

/**
 * Skeleton for the Creating grid. Mirrors `.creating-grid` cells with a
 * 4:3 thumb plus the kind / title / time meta block.
 */
export function CreatingGridSkeleton({ cells = 6 }) {
  return (
    <SkeletonShell label="Loading works">
      <ul className="skeleton-grid">
        {Array.from({ length: cells }, (_, i) => (
          <li key={i} className="skeleton-grid-cell">
            <Skeleton className="skeleton-grid-thumb" />
            <div className="skeleton-grid-meta">
              <Skeleton className="skeleton-grid-kind" />
              <Skeleton
                className="skeleton-grid-title"
                style={{ width: `${55 + ((i * 9) % 30)}%` }}
              />
              <Skeleton className="skeleton-grid-time" />
            </div>
          </li>
        ))}
      </ul>
    </SkeletonShell>
  );
}

/**
 * Skeleton for a single record page (Record.jsx). Renders a simple body
 * stub plus a thin meta row that mirrors `.record-page-meta`.
 */
export function RecordSkeleton() {
  return (
    <SkeletonShell label="Loading record">
      <div className="skeleton-record">
        <SkeletonText lines={3} lineHeight="1.05em" lastLineWidth="55%" />
        <div className="skeleton-record-meta">
          <Skeleton className="skeleton-record-meta-cell" />
          <Skeleton className="skeleton-record-meta-cell" style={{ width: '3.5rem' }} />
          <Skeleton className="skeleton-record-meta-cell" style={{ width: '6rem' }} />
        </div>
      </div>
    </SkeletonShell>
  );
}

// DocumentSkeleton's text (lines per paragraph, the last line's length) and
// its band (each column's label and value widths).
const DOC_PARAGRAPHS = [
  { lines: 4, last: '46%' },
  { lines: 6, last: '62%' },
  { lines: 5, last: '38%' },
];
const DOC_FIGURE = 'figure';

const DOC_BAND = {
  essay: [
    ['4.5em', '7em'],
    ['6em', '4.5em'],
    ['3.5em', '6.5em'],
  ],
  work: [
    ['4em', '7em'],
    ['3.5em', '4.5em'],
    ['3.5em', '5em'],
  ],
};

/**
 * Skeleton for a standard.site document page: an essay at /blogging/:slug
 * (`kind="essay"`) or a work at /creating/:slug (`kind="work"`). Both are the
 * same three things in the same places as the page that replaces them: the
 * title, the ruled band of facts under it, and the opening of the text.
 *
 * The shell wears the page's own classes rather than copies of their numbers,
 * so it sits in the column the document will: `.blog-article.blog-book` hangs
 * an essay from the chrome's left edge in the 28em prose measure, and
 * `.creating-work-page` centres a work in the reading measure (Blogging.css,
 * Creating.css — both pages import those sheets). The title is the page's
 * `.page-title`, the band is a real `.document-meta` with bars in its cells,
 * and the text sits in `.leaflet-doc.reading`, so the size, leading and
 * measure are the essay setting's own: each bar line is one line of the text,
 * a drop cap spans the first three, and later paragraphs open on the 1.5em
 * indent.
 *
 * A work gets a figure after its first paragraph, as most of the portfolio
 * has (a cover, a screenshot, a link card) within its opening lines.
 */
export function DocumentSkeleton({ kind = 'essay' }) {
  const essay = kind === 'essay';
  const blocks = essay
    ? DOC_PARAGRAPHS
    : [DOC_PARAGRAPHS[0], DOC_FIGURE, ...DOC_PARAGRAPHS.slice(1)];
  const band = (
    <dl className="document-meta" data-columns="3" aria-hidden="true">
      {DOC_BAND[essay ? 'essay' : 'work'].map(([labelWidth, valueWidth], i) => (
        <div key={i} className="document-meta-pair">
          <dt className="document-meta-label">
            <Skeleton width={labelWidth} height="0.75em" />
          </dt>
          <dd className="document-meta-value">
            <Skeleton width={valueWidth} height="0.7em" />
          </dd>
        </div>
      ))}
    </dl>
  );
  return (
    <SkeletonShell
      label={essay ? 'Loading essay' : 'Loading work'}
      className={essay ? 'blog-article blog-book' : 'creating-work-page'}
    >
      <h1 className={`page-title${essay ? ' blog-title' : ''}`} aria-hidden="true">
        <Skeleton width={essay ? '72%' : '45%'} height="0.6em" />
      </h1>
      {essay ? <aside className="blog-margin">{band}</aside> : band}
      <div className="leaflet-doc reading skeleton-doc" aria-hidden="true">
        {blocks.map((block, b) =>
          block === DOC_FIGURE ? (
            <Skeleton key={b} block className="skeleton-doc-figure" height="auto" />
          ) : (
            <div key={b} className="skeleton-doc-para">
              {/* Three lines of the text, cap-top to third baseline: `lh`
                  here is the paragraph's leading, since the cap's class
                  inherits it rather than taking `.skeleton`'s 1. */}
              {b === 0 && (
                <Skeleton block className="skeleton-doc-cap" height="calc(2lh + 0.6em)" />
              )}
              {Array.from({ length: block.lines }, (_, l) => (
                <span key={l} className="skeleton-doc-line">
                  <Skeleton width={l === block.lines - 1 ? block.last : '100%'} height="0.6em" />
                </span>
              ))}
            </div>
          ),
        )}
      </div>
    </SkeletonShell>
  );
}

/**
 * Skeleton for an album page (ListeningAlbum): meta, summary, image, body. The
 * creating work page it was named for draws DocumentSkeleton now.
 */
export function CreatingWorkSkeleton() {
  return (
    <SkeletonShell label="Loading work">
      <div className="skeleton-record">
        <div className="skeleton-record-meta">
          <Skeleton className="skeleton-record-meta-cell" style={{ width: '4rem' }} />
          <Skeleton className="skeleton-record-meta-cell" style={{ width: '6.5rem' }} />
        </div>
        <SkeletonText lines={2} lineHeight="1em" lastLineWidth="45%" />
        <Skeleton className="skeleton-record-image" block />
        <SkeletonText lines={4} lineHeight="0.95em" lastLineWidth="55%" />
      </div>
    </SkeletonShell>
  );
}

/**
 * Skeleton for the Mothing page. Mirrors the real layout so nothing
 * reflows once the snapshot lands: a `.mothing-stats` row, then a couple
 * of session blocks — each a `.mothing-session-head` above a
 * `.mothing-grid` of 1:1 moth cards (thumb + name / sci / time meta).
 */
export function MothingSkeleton({ sessions = 2, cells = 4 }) {
  return (
    <SkeletonShell label="Loading moths">
      <div className="skeleton-mothing-stats">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="skeleton-mothing-stat">
            <Skeleton
              className="skeleton-mothing-stat-value"
              style={{ width: i === 3 ? '9rem' : `${2.5 + ((i * 5) % 3)}rem` }}
            />
            <Skeleton className="skeleton-mothing-stat-label" />
          </div>
        ))}
      </div>
      <div className="skeleton-mothing-sessions">
        {Array.from({ length: sessions }, (_, s) => (
          <section key={s} className="skeleton-mothing-session">
            <div className="skeleton-mothing-head">
              <div className="skeleton-mothing-headrow">
                <Skeleton className="skeleton-mothing-num" />
                <Skeleton
                  className="skeleton-mothing-title"
                  style={{ width: `${9 + ((s * 4) % 5)}rem` }}
                />
              </div>
              <Skeleton className="skeleton-mothing-headstats" />
            </div>
            <ul className="skeleton-mothing-grid">
              {Array.from({ length: cells }, (_, i) => (
                <li key={i} className="skeleton-mothing-cell">
                  <Skeleton className="skeleton-mothing-thumb" />
                  <div className="skeleton-mothing-meta">
                    <Skeleton
                      className="skeleton-mothing-name"
                      style={{ width: `${55 + ((i * 9) % 30)}%` }}
                    />
                    <Skeleton
                      className="skeleton-mothing-sci"
                      style={{ width: `${40 + ((i * 7) % 25)}%` }}
                    />
                    <Skeleton className="skeleton-mothing-sub" />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </SkeletonShell>
  );
}

/**
 * Skeleton for a comments tree. Mirrors the .comment layout (avatar +
 * byline + body) so the placeholder doesn't reflow once replies arrive.
 */
export function CommentsSkeleton({ rows = 3 }) {
  return (
    <SkeletonShell label="Loading replies">
      <ul className="skeleton-comments">
        {Array.from({ length: rows }, (_, i) => (
          <li key={i} className="skeleton-comments-item">
            <div className="skeleton-comments-head">
              <Skeleton className="skeleton-comments-avatar" />
              <Skeleton
                className="skeleton-comments-name"
                style={{ width: `${30 + ((i * 13) % 25)}%` }}
              />
            </div>
            <SkeletonText
              className="skeleton-comments-text"
              lines={1 + (i % 2)}
              lineHeight="0.95em"
              lastLineWidth={`${45 + ((i * 11) % 30)}%`}
            />
          </li>
        ))}
      </ul>
    </SkeletonShell>
  );
}

/**
 * Generic prose skeleton — used for the Sharing page (and any other
 * Markdown-only page in the future). Just stacked paragraph blocks.
 */
export function ProseSkeleton({ paragraphs = 3 }) {
  return (
    <SkeletonShell label="Loading">
      <div className="skeleton-record">
        {Array.from({ length: paragraphs }, (_, i) => (
          <SkeletonText
            key={i}
            lines={3 + (i % 2)}
            lineHeight="0.95em"
            lastLineWidth={`${45 + ((i * 13) % 30)}%`}
          />
        ))}
      </div>
    </SkeletonShell>
  );
}

/**
 * Skeleton for the "for hire" resume page (Resume.jsx). Mirrors the
 * `.resume` article — a header (title + headline + contact row), a summary
 * paragraph, and the Experience / Education / Skills sections — so the swap
 * to the real resume doesn't reflow. Experience orgs carry the same left
 * rule and role head (title beside a right-aligned date range) as the real
 * layout; skills lay out on the same label + chips grid.
 */
export function ResumeSkeleton({ orgs = 3, education = 1, skillGroups = 3 }) {
  return (
    <SkeletonShell label="Loading resume" className="skeleton-resume reveal">
      <header className="skeleton-resume-header">
        <Skeleton className="skeleton-resume-title" />
        <Skeleton className="skeleton-resume-headline" />
        <div className="skeleton-resume-contact">
          <Skeleton className="skeleton-resume-contact-item" style={{ width: '5rem' }} />
          <Skeleton className="skeleton-resume-contact-item" style={{ width: '9.5rem' }} />
          <Skeleton className="skeleton-resume-contact-item" style={{ width: '6.5rem' }} />
        </div>
      </header>

      <div className="skeleton-resume-summary">
        <SkeletonText lines={3} lineHeight="0.9em" lastLineWidth="52%" />
      </div>

      {/* Experience */}
      <section className="skeleton-resume-section">
        <div className="skeleton-resume-section-head">
          <Skeleton className="skeleton-resume-section-title" style={{ width: '6rem' }} />
        </div>
        <div className="skeleton-resume-orgs">
          {Array.from({ length: orgs }, (_, oi) => (
            <div key={oi} className="skeleton-resume-org">
              <Skeleton
                className="skeleton-resume-org-name"
                style={{ width: `${9 + ((oi * 5) % 6)}rem` }}
              />
              <div className="skeleton-resume-role">
                <div className="skeleton-resume-role-head">
                  <Skeleton
                    className="skeleton-resume-role-title"
                    style={{ width: `${8 + ((oi * 7) % 7)}rem` }}
                  />
                  <Skeleton className="skeleton-resume-role-dates" />
                </div>
                <Skeleton
                  className="skeleton-resume-role-meta"
                  style={{ width: `${10 + ((oi * 3) % 6)}rem` }}
                />
                <ul className="skeleton-resume-highlights">
                  {Array.from({ length: 2 + (oi % 2) }, (_, hi) => (
                    <li key={hi} className="skeleton-resume-highlight">
                      <Skeleton block style={{ width: `${72 + ((hi * 13) % 24)}%`, height: '0.85em' }} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Education */}
      <section className="skeleton-resume-section">
        <div className="skeleton-resume-section-head">
          <Skeleton className="skeleton-resume-section-title" style={{ width: '5.5rem' }} />
        </div>
        <div className="skeleton-resume-orgs">
          {Array.from({ length: education }, (_, ei) => (
            <div key={ei} className="skeleton-resume-role">
              <div className="skeleton-resume-role-head">
                <Skeleton
                  className="skeleton-resume-role-title"
                  style={{ width: `${10 + ((ei * 5) % 5)}rem` }}
                />
                <Skeleton className="skeleton-resume-role-dates" />
              </div>
              <Skeleton className="skeleton-resume-role-meta" style={{ width: '8rem' }} />
            </div>
          ))}
        </div>
      </section>

      {/* Skills */}
      <section className="skeleton-resume-section">
        <div className="skeleton-resume-section-head">
          <Skeleton className="skeleton-resume-section-title" style={{ width: '4rem' }} />
        </div>
        <div className="skeleton-resume-skills">
          {Array.from({ length: skillGroups }, (_, gi) => (
            <div key={gi} className="skeleton-resume-skill-group">
              <Skeleton
                className="skeleton-resume-skill-cat"
                style={{ width: `${5 + ((gi * 3) % 5)}rem` }}
              />
              <div className="skeleton-resume-skill-items">
                {Array.from({ length: 3 + (gi % 3) }, (_, si) => (
                  <Skeleton
                    key={si}
                    className="skeleton-resume-skill"
                    style={{ width: `${3 + ((si * 7) % 5)}rem` }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </SkeletonShell>
  );
}

/* -------------------------------------------------------------------- */
/* Admin variants                                                        */
/* -------------------------------------------------------------------- */

/**
 * Skeleton for an admin record list. There are two real row shapes in the
 * admin now and a single placeholder cannot stand in for both, so the caller
 * says which one it is about to render:
 *
 *   `variant="classic"` (default) — the frozen `.admin-record-list` row: a
 *     fixed 14ch rkey column, then a flexible preview line, all on one line.
 *     Still what PagesOverview draws.
 *   `variant="workbench"` — the workbench list column's `.wb-list-row`: a
 *     checkbox gutter, then the record's name on its own line with the rkey
 *     and timestamp on a quieter line beneath it.
 *
 * Guessing wrong is not a cosmetic matter: the two shapes put their heaviest
 * block on opposite sides of the column, so a mismatched placeholder reads as
 * the list jumping sideways the instant the real rows arrive.
 *
 * Pass `toolbar` to prepend a `.admin-toolbar`-shaped bar, used when the
 * whole page is still resolving (e.g. the session-restore gate) and the
 * real toolbar hasn't rendered yet.
 */
export function AdminRecordListSkeleton({
  rows = 6,
  toolbar = false,
  variant = 'classic',
  label = 'Loading records',
}) {
  const workbench = variant === 'workbench';
  return (
    <SkeletonShell label={label}>
      {toolbar && (
        <div className="skeleton-admin-toolbar">
          <Skeleton className="skeleton-admin-toolbar-back" />
          <Skeleton className="skeleton-admin-toolbar-nsid" />
          <Skeleton className="skeleton-admin-toolbar-btn" />
        </div>
      )}
      <ul className="skeleton-admin-list">
        {Array.from({ length: rows }, (_, i) =>
          workbench ? (
            <li key={i} className="skeleton-admin-row skeleton-admin-row-wb">
              {/* A real checkbox, hidden with `visibility` so it still occupies
                  its box. Two reasons it is not a plain <span> of a guessed
                  width: the browser's own checkbox metrics (13px plus 4px/3px
                  default margins) are what set the real gutter, and copying
                  them as a literal would silently drift; and the loaded row
                  keeps its checkbox at opacity 0 until you hover, focus or tick
                  it, so a breathing block here would advertise a control the
                  resolved list does not show. `disabled` and the negative
                  tabindex keep it out of the tab order. */}
              <input
                type="checkbox"
                className="skeleton-admin-gutter"
                disabled
                tabIndex={-1}
                aria-hidden="true"
                readOnly
              />
              {/* Heights are passed as props, not left to the stylesheet.
                  `Skeleton` writes its `height` default as an INLINE style, so
                  an inline rule always beats a `.skeleton-*` class and a height
                  declared in Skeleton.css silently does nothing. Widths are the
                  other way round — the `width` prop defaults to undefined, so
                  those do belong in the class. */}
              <span className="skeleton-admin-stack">
                <span className="skeleton-admin-line">
                  <Skeleton
                    className="skeleton-admin-label"
                    height="calc(var(--text-base) * var(--leading-tight))"
                    style={{ width: `${52 + ((i * 13) % 40)}%` }}
                  />
                </span>
                <span className="skeleton-admin-meta">
                  <Skeleton className="skeleton-admin-when" height="1.2rem" />
                  <Skeleton className="skeleton-admin-rkey-inline" height="1.2rem" />
                </span>
              </span>
            </li>
          ) : (
            <li key={i} className="skeleton-admin-row">
              {/* Heights as PROPS, for the reason spelled out in the workbench
                  branch above: `Skeleton` writes `height` as an inline style, so
                  a height in the stylesheet is silently dead. Both numbers are
                  the real row's measured line boxes — 1.35rem is the `<code>`
                  rkey chip's 21.6px, 1.6rem the `.admin-record-preview`'s 25.6px
                  — which is what brings the row from 36.2px to `.admin-record-
                  link`'s 45.8px. Every one of this variant's callers had a real
                  row TALLER than the placeholder (45.8 on Site pages, 39.4 on
                  Listening, 56.4 in the nav menu, 73.7 in the resume studio), so
                  the list shrank on every one of them as the data landed. */}
              <Skeleton className="skeleton-admin-rkey" height="1.35rem" />
              <Skeleton
                className="skeleton-admin-preview"
                height="1.6rem"
                /* 38–72% rather than 52–92%. With the flex-grow removed (see
                   Skeleton.css) this width is now what the bar actually gets,
                   and a bar that reaches the right edge promises a full-width
                   line of text where the real rows hold a short title with the
                   rest of the row empty — the play list's rows are a label at
                   the left and a small "Edit →" at the right with ~300px of
                   nothing between them. */
                style={{ width: `${38 + ((i * 13) % 34)}%` }}
              />
            </li>
          ),
        )}
      </ul>
    </SkeletonShell>
  );
}

/**
 * Skeleton for the record editor form (RecordEditor while it fetches an
 * existing record). Mirrors the `.admin-form` stack — small-caps field
 * labels above inputs, one tall block for the body textarea, and an
 * actions row.
 *
 * `fields` is a count and gets the historical shape: every field a short input
 * except the last, which is drawn as a textarea. That is a GUESS, and it is
 * wrong whenever the lexicon has no long-text field at all — on an `is.dame.now`
 * record the placeholder draws two 40px inputs and a 136px block, then resolves
 * to three short fields and no textarea, so the form visibly collapses by ~96px
 * and everything under it jumps.
 *
 * `shapes` is the way out: pass an array of `'short'` / `'tall'`, one per field,
 * and the placeholder draws exactly the form that is coming. The caller is the
 * only one that can know — RecordEditor has the lexicon in hand at the call site
 * and can map its field types — so the knob is here and the knowledge stays
 * there. It is opt-in rather than the default because this component also
 * renders on public routes (the quick-edit sheet portals to <body>, and
 * /exploring draws the same form), where the loading state has to stay exactly
 * as it has always looked.
 */
export function AdminEditorSkeleton({ fields = 4, shapes = null }) {
  const rows = Array.isArray(shapes) && shapes.length ? shapes : null;
  const count = rows ? rows.length : fields;
  return (
    <SkeletonShell label="Loading record" className="skeleton-admin-form">
      {Array.from({ length: count }, (_, i) => {
        const tall = rows ? rows[i] === 'tall' : i === count - 1;
        return (
          <div key={i} className="skeleton-admin-field">
            <Skeleton
              className="skeleton-admin-field-label"
              style={{ width: `${4 + ((i * 5) % 5)}rem` }}
            />
            <Skeleton
              className={`skeleton-admin-field-input${tall ? ' skeleton-admin-field-input-tall' : ''}`}
            />
          </div>
        );
      })}
      <div className="skeleton-admin-actions">
        <Skeleton className="skeleton-admin-action-btn" />
        <Skeleton className="skeleton-admin-action-btn" style={{ width: '4.5rem' }} />
      </div>
    </SkeletonShell>
  );
}
